import { CircularProgress, CssBaseline, Paper } from '@mui/material';
import { createBrowserHistory } from 'history';
import { action, computed, configure, reaction, when, makeObservable } from 'mobx';
import { Provider, observer } from 'mobx-react';
import React, { Fragment, Suspense } from 'react';
import { Route, Routes } from 'react-router';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';


import AppLoadingBar from './AppLoadingBar';
import CarregamentoRota from './components/CarregamentoRota';
import Rota from './Rota';
import SnackBar from './SnackBar';
import TopBar from './TopBar';
import ControleGabinete from './components/controle-gabinete/ControleGabinete';
import EspelhoProcesso from './components/EspelhoProcesso';
import ResumoAguiaGeracaoToolbar from './components/escaninho/aguia/ResumoAguiaGeracaoToolbar';
import ModalListaDeAutotexto from './components/modelos/ModalListaDeAutotexto';
import ParametroGabinete from './components/parametros/ParametroGabinete';
import ParametroUsuario from './components/parametros/ParametroUsuario';
import './css/AppStyle.css';
import './css/app.css';
import './css/opensans.css';
import MenuPrincipal from './misc/MenuPrincipal';
import ModalConfirm from './misc/ModalConfirm';
import ReactionsEscaninho from './reactions/ReactionsEscaninho';
import AxiosConfig from './services/AxiosConfig';
import ClienteEnvelopeService from './services/ClienteEnvelopeService';
import EscaninhoService from './services/EscaninhoService';
import LogusService from './services/LogusService';
import UsuarioStore from './store/UsuarioStore';
import { AppStore } from './store/AppStore';
import RootStore from './store/RootStore';
import RouterStore from './store/RouterStore';
import Configuracao from './utils/Configuracao';
import ErrorBoundary from './utils/ErrorBoundaries';
import { aplicarSsoTokenDaUrl, possuiSsoTokenStorage } from './utils/SsoCookieUtils';

// Função auxiliar para lazy loading com retry
function lazyWithRetry(importFunc: () => Promise<any>) {
    return React.lazy(() =>
        importFunc().catch(err => {
            console.error('Erro ao carregar componente:', err);
            throw err;
        })
    );
}

// Lazy-loaded components com retry
const InformacoesAplicacao = lazyWithRetry(() => import('./components/InformacoesAplicacao'));
const PaginaNaoEncontrada = lazyWithRetry(() => import('./PaginaNaoEncontrada'));
const InicialAssistenteMinutas = lazyWithRetry(() => import('./components/assistente-minutas/InicialAssistenteMinutas'));
const Temas = lazyWithRetry(() => import('./assistente-minutas/components/Temas'));
const Autotextos = lazyWithRetry(() => import('./assistente-minutas/components/Autotextos'));
const ModelosTemas = lazyWithRetry(() => import('./assistente-minutas/components/ModelosTemas'));
const EstruturaDeDocumento = lazyWithRetry(() => import('./assistente-minutas/components/EstruturaDeDocumento'));
const GerenciarMinutas = lazyWithRetry(() => import('./assistente-minutas/components/GerenciarMinutas'));
const EditarGerenciarMinutas = lazyWithRetry(() => import('./assistente-minutas/components/EditarGerenciarMinutas'));
const Categoria = lazyWithRetry(() => import('./components/categorias/Categoria'));
const ConfiguracaoGabinete = lazyWithRetry(() => import('./components/controle-gabinete/ConfiguracaoGabinete'));
const GerenciamentoAcervo = lazyWithRetry(() => import('./components/gerenciamento-acervo/GerenciamentoAcervo'));
const ImpressaoEspelhoProcesso = lazyWithRetry(() => import('./components/ImpressaoEspelhoProcesso'));
const MinutasAssinadas = lazyWithRetry(() => import('./components/minutas-assinadas/MinutasAssinadas'));
const Modelos = lazyWithRetry(() => import('./components/modelos/Modelos'));
const PaginaEscaninho = lazyWithRetry(() => import('./containers/PaginaEscaninho'));
const PaginaEscaninhoVistos = lazyWithRetry(() => import('./containers/PaginaEscaninhoVistos'));
const PaginaEscaninhoJulgamento = lazyWithRetry(() => import('./containers/PaginaEscaninhoJulgamento'));
const ParametroSistema = lazyWithRetry(() => import('./components/parametros/ParametroSistema'));
const ResumoDocumentosAguia = lazyWithRetry(() => import('./components/escaninho/aguia/ResumoDocumentosAguia'));
const TelaPrincipal = lazyWithRetry(() => import('./misc/TelaPrincipal'));
const MovimentosCnj = lazyWithRetry(() => import('./components/movimentosCnj/MovimentosCnj'));

// History & stores
aplicarSsoTokenDaUrl();
const history = createBrowserHistory();
const rootStore = new RootStore(history);
RouterStore.inicializar(history);

// Silenciar avisos de MobX/MUI durante dev
const originalWarn = console.warn;
console.warn = (...args) => {
    const [msg, ...rest] = args;
    const ignorarReactRouter = typeof msg === 'string' && (
        msg.includes('React Router Future Flag Warning') ||
        msg.includes("Warning: Can't perform a React state update on a component")
    );
    const ignorarMUI = typeof msg === 'string' && (
        msg.includes('MUI:') || msg.includes('Material-UI:')
    );
    if (ignorarReactRouter || ignorarMUI) return;
    originalWarn(msg, ...rest);
};

class App extends React.Component {
    constructor(props: {}) {
        super(props);

        makeObservable(this, {
            abrirApp: action,
            renderApp: computed
        });

        this.configurarMobx();
        AxiosConfig.configurarAxios();
        if (!RouterStore.isRotaResumoDocumentosAguia || (RouterStore.isRotaResumoDocumentosAguia && !possuiSsoTokenStorage())) {
            LogusService.inicializarLogus();
        }
    }

    configurarMobx() {
        configure({
            enforceActions: 'never',
            computedRequiresReaction: false,
            reactionRequiresObservable: false,
        });
    }

    async carregarProcessosEReactions() {
        await EscaninhoService.carregarProcessosDoGabinete();
        // Os filtros liberam a lista assim que a base de processos está pronta.
        // Dados BTV não são necessários para montar os processos do escaninho.
        ReactionsEscaninho.iniciarReactions();
        ReactionsEscaninho.ajustesAoCarregarEscaninho();
        // BTV desativado: os dados retornados não têm consumidores no frontend.
        // await InformacoesProcessoService.buscarDadosProcessoBTV(UsuarioStore.gabinete);
    }

    async inicializarApp() {
        if (RouterStore.isRotaIndependente) {
            rootStore.inicializarStoresEspelho();
            return;
        }

        await rootStore.inicializarStoresSistema();

        await ClienteEnvelopeService.acompanharGabinete();
        if (!UsuarioStore.gabinete) {
            when(() => UsuarioStore.gabinete != undefined, async () => {
                await this.carregarProcessosEReactions();
            });
        } else {
            await this.carregarProcessosEReactions();
        }
    }

    componentDidMount() {
        AppStore.larguraJanela = window.innerWidth;
    }

    abrirApp = () => {
        if (UsuarioStore.usuario) {
            if (!UsuarioStore.token) {
                AppStore.fecharApp();
            }
        }
    };

    reactionCarregarApp = reaction(() => AppStore.carregarApp, async (carregarApp) => {
        if (!carregarApp) {
            AxiosConfig.setAuthorization('');
            rootStore.limparStores();
            LogusService.logusLogout();
        } else {
            AxiosConfig.configurarAxios();
            this.abrirApp();
            this.inicializarApp();
        }
    })

    get renderApp() {
        if (!UsuarioStore.isUsuarioLogado && !RouterStore.isRotaResumoDocumentosAguia) {
            return <h2 style={{ marginLeft: '15px' }}>Redirecionando...</h2>;
        }

        if (!UsuarioStore.gabinete && !RouterStore.isRotaIndependente) {
            return <h2 style={{ marginLeft: '15px' }}>Carregando dados do perfil...</h2>;
        }

        return (
            <div
                className='alturaWindow'
                style={
                    RouterStore.isRotaResumoDocumentosAguia
                        ? { height: '100vh', minHeight: '100vh' }
                        : RouterStore.isRotaEspelho
                            ? { height: 'calc(100vh - 60px)' }
                            : undefined
                }
            >
                {!RouterStore.isRotaIndependente &&
                    <div id='div-menu-principal' style={{ position: 'relative' }}>
                        <MenuPrincipal />
                    </div>
                }
                <div id='paper' style={{ position: 'relative', width: '100%' }} className='zoom-escaninho'>
                    <Paper
                        elevation={RouterStore.isImpressao || RouterStore.isRotaResumoDocumentosAguia ? 0 : 2}
                        style={{
                            overflowY: 'auto',
                            height: '100%',
                            ...(RouterStore.isRotaResumoDocumentosAguia ? { boxShadow: 'none' } : {})
                        }}
                    >
                        <HistoryRouter history={RouterStore.history} useTransitions={false}>
                            <Suspense fallback={<CarregamentoRota />}>
                                <Routes>
                                    <Route path="/" element={<Rota element={<TelaPrincipal />} />} />
                                    <Route path="/telaPrincipal" element={<Rota element={<TelaPrincipal />} />} />
                                    {UsuarioStore.usuarioTemPapel(Configuracao.SUPER_ADMINISTRADOR) &&
                                        <Route path="/parametroSistema" element={<ParametroSistema />} />}
                                    {UsuarioStore.usuarioTemPapel(Configuracao.SUPER_ADMINISTRADOR) &&
                                        <Route path="/movimentosCnj" element={<MovimentosCnj />} />}
                                    <Route path="/parametroGabinete" element={<Rota element={<ParametroGabinete />} />} />
                                    <Route path="/parametroUsuario" element={<Rota element={<ParametroUsuario />} />} />
                                    <Route path="/escaninho/dashboard" element={<Rota element={<GerenciamentoAcervo />} />} />
                                    <Route path="/controleGabinete" element={<Rota element={<ControleGabinete />} />} />
                                    <Route path="/configuracaoGabinete" element={<Rota element={<ConfiguracaoGabinete />} />} />
                                    <Route path="/categoria" element={<Rota element={<Categoria />} />} />
                                    <Route path="/modelosMinuta" element={<Rota element={<Modelos />} />} />
                                    <Route path="/minutasAssinadas" element={<Rota element={<MinutasAssinadas />} />} />
                                    <Route path="/espelhoProcesso/:idProcesso" element={<Rota element={<EspelhoProcesso />} />} />
                                    <Route path="/impressaoEspelhoProcesso/:idProcesso" element={<Rota element={<ImpressaoEspelhoProcesso />} />} />
                                    <Route path="/resumoDocumentosAguia/:idProcesso" element={<Rota element={<ResumoDocumentosAguia />} />} />
                                    <Route path="/variaveis" element={<Rota element={<ModalListaDeAutotexto />} />} />
                                    <Route path="/escaninho/assinatura" element={<Rota params={{ tipo: 'assinaturaNova' }} element={<PaginaEscaninho />} />} />
                                    <Route path="/escaninho/vistos" element={<Rota params={{ tipo: 'vistos' }} element={<PaginaEscaninhoVistos />} />} />
                                    <Route path="/escaninho/julgamento" element={<Rota params={{ tipo: 'julgamento' }} element={<PaginaEscaninhoJulgamento />} />} />
                                    <Route path="/escaninho/pauta" element={<Rota params={{ tipo: 'pauta' }} element={<PaginaEscaninho />} />} />
                                    <Route path="/escaninho/peticao" element={<Rota params={{ tipo: 'peticao' }} element={<PaginaEscaninho />} />} />
                                    <Route path="/escaninho/:tipo" element={<Rota element={<PaginaEscaninho />} />} />
                                    <Route path="/escaninho2/:tipo" element={<Rota element={<PaginaEscaninho />} />} />
                                    <Route path="/assistenteMinutas/temas" element={<Rota element={<Temas />} />} />
                                    <Route path="/assistenteMinutas/autotextos" element={<Rota element={<Autotextos />} />} />
                                    <Route path="/assistenteMinutas/modelosTemas" element={<Rota element={<ModelosTemas />} />} />
                                    <Route path="/assistenteMinutas/estruturas" element={<Rota element={<EstruturaDeDocumento />} />} />
                                    <Route path="/assistenteMinutas/gerenciarMinutas/editar/:tipoMinuta/:idMinuta/:numProcesso" element={<Rota element={<EditarGerenciarMinutas />} />} />
                                    <Route path="/assistenteMinutas/gerenciarMinutas" element={<Rota element={<GerenciarMinutas />} />} />
                                    <Route path="/assistenteMinutas" element={<Rota element={<InicialAssistenteMinutas />} />} />
                                    <Route path="*" element={<Rota element={<PaginaNaoEncontrada />} />} />
                                </Routes>
                            </Suspense>
                        </HistoryRouter>
                    </Paper>
                    <ModalConfirm />
                </div>
            </div>
        );
    }

    render() {
        return (
            <Provider stores={rootStore}>
                <ErrorBoundary>
                    <CssBaseline />
                    <Fragment>
                        <div id="div-container" style={{ display: 'flex', flexDirection: 'column', height: 'inherit' }}>
                            {!RouterStore.isRotaResumoDocumentosAguia && <div><TopBar /></div>}
                            <div style={{ position: 'relative' }}>
                                <AppLoadingBar />
                                {this.renderApp}
                                {!RouterStore.isRotaIndependente &&
                                    <Suspense fallback={
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                                            <CircularProgress />
                                        </div>
                                    }>
                                        <InformacoesAplicacao exibirNome={false} exibirDependencias={true} />
                                    </Suspense>
                                }
                            </div>
                        </div>
                    </Fragment>
                    <ResumoAguiaGeracaoToolbar />
                    <SnackBar />
                </ErrorBoundary>
            </Provider>
        );
    }
}

export default observer(App);
